import type {
  ChangeEventHandler,
  ComponentProps,
  Key,
  ReactNode,
  RefObject,
} from 'react';

import { ClickAwayListener, Popper, TextareaAutosize } from '@mui/material';
import { cn } from 'cn';
import { useEffect, useId, useRef, useState } from 'react';

import { handleAutocompleteKeyDown } from './utils';

type TextareaProps = ComponentProps<typeof TextareaAutosize>;

export type AutocompleteTextareaRenderProps = {
  ref: RefObject<HTMLTextAreaElement | null>;
  textareaProps: Pick<
    TextareaProps,
    | 'aria-activedescendant'
    | 'aria-autocomplete'
    | 'aria-controls'
    | 'aria-expanded'
    | 'onChange'
    | 'onKeyDown'
    | 'role'
  >;
};

type AutocompleteTextareaProps<T> = {
  anchorEl: HTMLTextAreaElement | null;
  children: (props: AutocompleteTextareaRenderProps) => ReactNode;
  getOptionKey: (option: T) => Key;
  isOpen: boolean;
  onChange: ChangeEventHandler<HTMLTextAreaElement>;
  onClose: () => void;
  onSelect: (option: T) => boolean;
  options: readonly T[];
  renderOption: (option: T) => ReactNode;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
};

export function AutocompleteTextarea<T>({
  anchorEl,
  children,
  getOptionKey,
  isOpen,
  onChange,
  onClose,
  onSelect,
  options,
  renderOption,
  textareaRef,
}: AutocompleteTextareaProps<T>) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const suggestionElementsRef = useRef<Array<HTMLButtonElement | null>>([]);
  const listboxId = useId();
  const activeIndex = Math.min(selectedIndex, Math.max(options.length - 1, 0));

  useEffect(() => {
    suggestionElementsRef.current[activeIndex]?.scrollIntoView({
      block: 'nearest',
    });
  }, [activeIndex]);

  function close() {
    setSelectedIndex(0);
    onClose();
  }

  function selectOption(option: T) {
    if (!onSelect(option)) return;
    close();
  }

  return (
    <>
      <Popper
        anchorEl={anchorEl}
        open={isOpen}
        placement="top-start"
        sx={{ zIndex: 'modal' }}
      >
        <ClickAwayListener onClickAway={close}>
          <div
            className="mb-2 max-h-64 w-64 overflow-y-auto rounded-lg border border-tiga-border-lighter bg-white p-1"
            id={listboxId}
            role="listbox"
          >
            {options.map((option, index) => (
              <button
                key={getOptionKey(option)}
                aria-selected={index === activeIndex}
                className={cn(
                  'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-gray-100',
                  { 'bg-gray-100': index === activeIndex },
                )}
                id={`${listboxId}-option-${index}`}
                ref={(element) => {
                  suggestionElementsRef.current[index] = element;
                }}
                role="option"
                type="button"
                onClick={() => selectOption(option)}
              >
                {renderOption(option)}
              </button>
            ))}
          </div>
        </ClickAwayListener>
      </Popper>
      {children({
        ref: textareaRef,
        textareaProps: {
          'aria-activedescendant': isOpen
            ? `${listboxId}-option-${activeIndex}`
            : undefined,
          'aria-autocomplete': 'list',
          'aria-controls': isOpen ? listboxId : undefined,
          'aria-expanded': isOpen,
          onChange: (event) => {
            setSelectedIndex(0);
            onChange(event);
          },
          onKeyDown: (event) =>
            handleAutocompleteKeyDown(
              event,
              isOpen,
              options,
              activeIndex,
              setSelectedIndex,
              selectOption,
              close,
            ),
          role: 'combobox',
        },
      })}
    </>
  );
}
